/**
 * Integrytas Group - Base App (MOBILE)
 * Page: CSM.SYSTEM.PARAMETERS — mobile-native.
 *
 * Touch-first counterpart of src/pages/admin/SystemParametersPage.tsx. A DISTINCT
 * presentation (dense card list + facet chips + bottom-sheet detail) over the SAME
 * data layer: reads via apiDb (csm_system_parameters). §16 — never the desktop
 * grid re-hosted in a phone viewport.
 *
 * This is a read-only viewer: a scannable key/value list where each row shows the
 * parameter's internal_code and a value preview with a public/private status pill,
 * and tapping a row opens a detail sheet with the FULL value plus its data type,
 * active state and description. Editing parameter values (including the protected
 * runtime keys and the bcrypt-only override password) stays on the desktop, so no
 * write path is exercised here.
 *
 * csm_system_parameters is a generic KV store with no `_tl` table — internal_code
 * is the (untranslated) identifier and there are no FK codes to resolve. The only
 * enum, data_type_code (STRING/INT/BOOL/JSON), is a CHECK enum whose label is
 * localized through i18n (§17.B11), never rendered as a bare literal.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, X, SlidersHorizontal } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { MobilePageHeader, MobileStatStrip, MobileContentCard, MobileCardRow, MobileDetailRow, MobileFilterChips, MobileStatusPill } from '../components';
import type { MobileLeadTone, MobilePillTone } from '../components';
import type { MobilePageComponentProps } from '../mobile_page_components';

type DataTypeCode = 'STRING' | 'INT' | 'BOOL' | 'JSON';

interface MobileParamRow {
  system_parameter_id: number;
  internal_code: string;
  parameter_value: string;
  data_type_code: DataTypeCode;
  is_active: 'Y' | 'N';
  is_public: 'Y' | 'N';
  is_secret: 'Y' | 'N';
  description: string | null;
}

// Placeholder shown for a secret parameter's value — matches the gateway's
// SECRET_PARAM_MASK. The mobile admin is a read-only viewer, so it never reveals
// a raw credential; the reveal path lives on the desktop System Parameters page.
const SECRET_MASK = '••••••••';

// Visibility facet — the headline status of the list is public vs private
// (is_public), mirroring the row pill so filtering matches what the user sees.
type VisibilityFacet = 'all' | 'public' | 'private';

// Active facet — an independent All/Active/Inactive chip row over is_active,
// defaulting to Active-only (§17.B12). Named ActiveFacet (not StatusFacet) so it
// reads distinctly alongside the visibility facet above.
type ActiveFacet = 'all' | 'active' | 'inactive';

const DATA_TYPE_CODES: readonly DataTypeCode[] = ['STRING', 'INT', 'BOOL', 'JSON'];

function isDataTypeCode(value: string): value is DataTypeCode {
  return (DATA_TYPE_CODES as readonly string[]).includes(value);
}

// Y/N → semantic pill tone (active/public map to the ok/no enums).
const ynPillTone = (yn: 'Y' | 'N'): MobilePillTone => (yn === 'Y' ? 'ok' : 'no');
// Public rows lead blue, internal rows lead slate.
const visibilityLeadTone = (isPublic: 'Y' | 'N'): MobileLeadTone => (isPublic === 'Y' ? 'blue' : 'slate');

export function MobileSystemParametersPage({ session }: MobilePageComponentProps) {
  // `session` is part of the mobile page contract; reads run as the authenticated
  // user through the gateway (RLS still applies), so it is not referenced directly.
  void session;
  const { t } = useTranslation();
  const [params, setParams] = useState<MobileParamRow[]>([]);
  const [filter, setFilter] = useState('');
  const [visibility, setVisibility] = useState<VisibilityFacet>('all');
  const [activeFacet, setActiveFacet] = useState<ActiveFacet>('active');
  const [selected, setSelected] = useState<MobileParamRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data, error: e } = await apiDb
        .from('csm_system_parameters')
        .select('system_parameter_id, internal_code, parameter_value, data_type_code, is_active, is_public, is_secret, description')
        .order('internal_code');
      if (e) throw e;

      type RawParam = {
        system_parameter_id: number;
        internal_code: string;
        parameter_value: string | null;
        data_type_code: string;
        is_active: 'Y' | 'N';
        is_public: 'Y' | 'N';
        is_secret: 'Y' | 'N';
        description: string | null;
      };
      const rows: MobileParamRow[] = ((data ?? []) as RawParam[]).map((r) => ({
        system_parameter_id: r.system_parameter_id,
        internal_code: r.internal_code,
        parameter_value: r.parameter_value ?? '',
        data_type_code: isDataTypeCode(r.data_type_code) ? r.data_type_code : 'STRING',
        is_active: r.is_active,
        is_public: r.is_public,
        is_secret: r.is_secret,
        description: r.description,
      }));
      setParams(rows);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { void reload(); }, []);

  // CHECK-enum label via i18n (§17.B11) — never the stored literal.
  const typeLabel = (code: DataTypeCode): string => t(`mobile.sysparams.type.${code}`, code);

  const valuePreview = (r: MobileParamRow): string => {
    // Secret values arrive redacted from the gateway; show the mask, never raw.
    if (r.is_secret === 'Y') return SECRET_MASK;
    if (r.data_type_code === 'BOOL') {
      const yes = r.parameter_value === 'Y' || r.parameter_value.toUpperCase() === 'TRUE';
      return yes ? t('common.yes', 'Yes') : t('common.no', 'No');
    }
    const v = r.parameter_value;
    if (!v) return t('mobile.sysparams.value_empty', '(empty)');
    return v.length > 80 ? v.slice(0, 77) + '…' : v;
  };

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return params.filter((r) => {
      if (visibility === 'public' && r.is_public !== 'Y') return false;
      if (visibility === 'private' && r.is_public !== 'N') return false;
      if (activeFacet === 'active' && r.is_active !== 'Y') return false;
      if (activeFacet === 'inactive' && r.is_active !== 'N') return false;
      if (!q) return true;
      return (
        r.internal_code.toLowerCase().includes(q) ||
        r.parameter_value.toLowerCase().includes(q) ||
        (r.description ?? '').toLowerCase().includes(q)
      );
    });
  }, [params, filter, visibility, activeFacet]);

  const facets: { key: VisibilityFacet; label: string }[] = [
    { key: 'all', label: t('mobile.sysparams.all', 'All') },
    { key: 'public', label: t('mobile.sysparams.public', 'Public') },
    { key: 'private', label: t('mobile.sysparams.internal', 'Internal') },
  ];

  const activeFacets: { key: ActiveFacet; label: string }[] = [
    { key: 'all', label: t('mobile.sysparams.all', 'All') },
    { key: 'active', label: t('common.boolean.active', 'Active') },
    { key: 'inactive', label: t('common.boolean.inactive', 'Inactive') },
  ];

  // §17.B4 alignment (defect): the desktop calls a non-public parameter "Internal";
  // mobile previously said "Private". Use one term — "Internal" — across surfaces.
  const visibilityLabel = (r: MobileParamRow): string =>
    r.is_public === 'Y' ? t('mobile.sysparams.public', 'Public') : t('mobile.sysparams.internal', 'Internal');

  const activeLabel = (r: MobileParamRow): string =>
    r.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive');

  // Presentation-only summary derived from the loaded rows (unfiltered set).
  const stats = useMemo(() => ({
    total: params.length,
    public: params.filter((r) => r.is_public === 'Y').length,
    secret: params.filter((r) => r.is_secret === 'Y').length,
  }), [params]);

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.sysparams.title', 'System Parameters')}
        description={t('mobile.sysparams.description', 'Platform configuration values (secret values are masked).')}
      />

      <MobileStatStrip
        stats={[
          { value: stats.total, label: t('mobile.sysparams.kpi_total', 'Total') },
          { value: stats.public, label: t('mobile.sysparams.kpi_public', 'Public') },
          { value: stats.secret, label: t('mobile.sysparams.kpi_secret', 'Secret') },
        ]}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.sysparams.search', 'Search parameters')}
          aria-label={t('mobile.sysparams.search', 'Search parameters')}
        />
      </div>

      <MobileFilterChips
        chips={facets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => visibility === v}
        onSelect={(v) => setVisibility(v as VisibilityFacet)}
      />

      <MobileFilterChips
        chips={activeFacets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => activeFacet === v}
        onSelect={(v) => setActiveFacet(v as ActiveFacet)}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.sysparams.none', 'No parameters match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((r) => (
            <MobileCardRow
              key={r.system_parameter_id}
              lead={<SlidersHorizontal />}
              leadTone={visibilityLeadTone(r.is_public)}
              title={r.internal_code}
              subtitle={`${typeLabel(r.data_type_code)} · ${valuePreview(r)}`}
              trailing={
                <>
                  <MobileStatusPill tone={ynPillTone(r.is_active)}>{activeLabel(r)}</MobileStatusPill>
                  <MobileStatusPill tone={ynPillTone(r.is_public)}>{visibilityLabel(r)}</MobileStatusPill>
                </>
              }
              chevron
              onClick={() => setSelected(r)}
            />
          ))}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet — shows the full parameter value */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => setSelected(null)} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.sysparams.detail', 'Parameter detail')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><SlidersHorizontal size={20} /></span>
              <span>
                <div className="m-nm">{selected.internal_code}</div>
                <div className="m-em">{typeLabel(selected.data_type_code)}</div>
              </span>
            </div>

            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.sysparams.visibility', 'Visibility')}
                value={<MobileStatusPill tone={ynPillTone(selected.is_public)}>{visibilityLabel(selected)}</MobileStatusPill>}
              />
              <MobileDetailRow
                label={t('mobile.sysparams.status', 'Status')}
                value={<MobileStatusPill tone={ynPillTone(selected.is_active)}>{selected.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}</MobileStatusPill>}
              />
              <MobileDetailRow
                label={t('mobile.sysparams.type', 'Data type')}
                value={typeLabel(selected.data_type_code)}
              />
            </MobileContentCard>

            <div className="m-fieldcard">
              <div className="m-formrow">
                <label>{t('mobile.sysparams.value', 'Value')}</label>
                <div className="m-formrow-ro" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'monospace' }}>
                  {selected.is_secret === 'Y'
                    ? SECRET_MASK
                    : selected.parameter_value
                      ? selected.parameter_value
                      : <span className="m-formrow-hint">{t('mobile.sysparams.value_empty', '(empty)')}</span>}
                </div>
                {selected.is_secret === 'Y' && (
                  <div className="m-formrow-hint">
                    {t('mobile.sysparams.secret_hint', 'Hidden credential — reveal on the desktop admin.')}
                  </div>
                )}
              </div>
              <div className="m-formrow">
                <label>{t('mobile.sysparams.description', 'Description')}</label>
                <div className="m-formrow-ro">
                  {selected.description
                    ? selected.description
                    : <span className="m-formrow-hint">{t('common.none', 'None')}</span>}
                </div>
              </div>
            </div>

            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-ghost" onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
                {t('common.close', 'Close')}
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
